import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AccessDenied, errorStatus } from '@/components/RouteError';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { MovementsTable } from '@/components/finance/MovementsTable';
import { AccountEditDialog } from '@/components/finance/AccountEditDialog';
import { OpeningBalanceDialog } from '@/components/finance/OpeningBalanceDialog';
import { BankCheckDialog } from '@/components/finance/BankCheckDialog';
import { EnvelopesPanel } from '@/components/finance/EnvelopesPanel';
import { errorMessage } from '@/lib/errors';
import {
  financeAccountsQuery,
  financeBankChecksQuery,
  financeCategoriesQuery,
  financeMovementsQuery,
  financeOpeningBalancesQuery,
  financeSettingsQuery,
} from '@/lib/finance';
import { usePermissions } from '@/lib/me';
import { formatMinor } from '@/lib/money';

const PAGE = 50;

export const Route = createFileRoute('/_app/finance/accounts/$id')({
  component: AccountDetailRoute,
});

// The route reads the param; the page takes it as a prop, so the page can be
// mounted in a test without a matched route.
function AccountDetailRoute() {
  const { id } = Route.useParams();
  return <AccountDetailPage id={id} />;
}

export function AccountDetailPage({ id }: { id: string }) {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState(false);
  const [baseline, setBaseline] = useState(false);
  const [check, setCheck] = useState(false);

  const accounts = useQuery(financeAccountsQuery(true));
  const categories = useQuery(financeCategoriesQuery(true));
  const settings = useQuery(financeSettingsQuery);
  const opening = useQuery(financeOpeningBalancesQuery(id));
  const checks = useQuery(financeBankChecksQuery(id, 10, 0));
  const movements = useQuery(
    financeMovementsQuery({ accountId: id, limit: PAGE, offset, includeReversed: true }),
  );

  if (errorStatus(accounts.error) === 403) return <AccessDenied />;
  const account = accounts.data?.find((a) => a.id === id);

  if (accounts.isLoading) return <p className="text-sm text-muted">Loading account…</p>;
  // A failed list is not an unknown id: reporting a 500 or a dropped
  // connection as «no account with this id» sends the operator hunting for a
  // typo in the URL while the server is down.
  if (accounts.isError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {errorMessage(accounts.error)}
      </p>
    );
  }
  if (!account) {
    return (
      <p role="alert" className="text-sm text-destructive">
        No account with this id.
      </p>
    );
  }

  const total = movements.data?.total ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-xl font-bold text-[var(--text-heading)]">{account.name}</h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted">
            {account.currencies.join(' · ')}
            {!account.is_active && <Badge variant="neutral">Inactive</Badge>}
          </p>
        </div>
        {can('finance.manage') && (
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
              Edit account
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setBaseline(true)}>
              Record baseline
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setCheck(true)}>
              Reconcile with the bank
            </Button>
          </div>
        )}
      </div>

      <section className="flex flex-col gap-3">
        <h2 className="font-serif text-lg font-bold text-[var(--text-heading)]">Baselines</h2>
        {opening.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage(opening.error)}
          </p>
        ) : opening.data && opening.data.length > 0 ? (
          <ul className="flex flex-col divide-y divide-[var(--border-hairline)] rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
            {opening.data.map((b) => (
              <li key={b.id} className="flex flex-wrap items-center gap-2 px-4 py-2.5 text-sm">
                <span className="text-muted">{b.as_of}</span>
                <span className="text-xs uppercase tracking-[0.06em] text-muted">{b.currency}</span>
                <span className="tabular-nums">{formatMinor(b.balance_minor, b.currency)}</span>
                {/* A superseded baseline is what the figure WAS (§5.2) — it
                    stays on the list, marked, because deleting history is
                    what a baseline exists to prevent. */}
                {b.superseded_by && <Badge variant="neutral">Superseded</Badge>}
                {b.note && <span className="text-muted">{b.note}</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted">
            No baseline yet — every balance for this account is estimated from payments alone.
          </p>
        )}
      </section>

      {checks.data && checks.data.items.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="font-serif text-lg font-bold text-[var(--text-heading)]">
            Bank reconciliations
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {checks.data.items.map((c) => (
              <Card key={c.id} className="p-4 text-sm">
                <p className="text-muted">
                  {c.as_of} · {c.currency}
                </p>
                <p className="mt-1 tabular-nums">
                  bank {formatMinor(c.bank_balance_minor, c.currency)} · ours{' '}
                  {formatMinor(c.computed_balance_minor, c.currency)}
                </p>
                <p
                  className={
                    c.diff_minor === 0
                      ? 'mt-1 font-semibold tabular-nums text-[var(--ollio-success)]'
                      : 'mt-1 font-semibold tabular-nums text-destructive'
                  }
                >
                  diff {formatMinor(c.diff_minor, c.currency)}
                </p>
              </Card>
            ))}
          </div>
        </section>
      )}

      <section className="flex flex-col gap-3">
        <h2 className="font-serif text-lg font-bold text-[var(--text-heading)]">Payments</h2>
        {movements.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage(movements.error)}
          </p>
        ) : movements.isLoading ? (
          <p className="text-sm text-muted">Loading payments…</p>
        ) : (
          <>
            <MovementsTable
              movements={movements.data?.items ?? []}
              accounts={accounts.data}
              categories={categories.data}
              onOpen={(mid) => void navigate({ to: '/finance/movements/$id', params: { id: mid } })}
            />
            <div className="flex items-center gap-3 text-sm">
              <Button
                variant="secondary"
                size="sm"
                disabled={offset === 0}
                // offset ≥ 0 is the schema's floor; Math.max keeps a click at
                // the first page from posting a negative offset (400).
                onClick={() => setOffset((o) => Math.max(0, o - PAGE))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={offset + PAGE >= total}
                onClick={() => setOffset((o) => o + PAGE)}
              >
                Next
              </Button>
              <span className="text-muted">
                {total === 0 ? 'none' : `${offset + 1}–${Math.min(offset + PAGE, total)} of ${total}`}
              </span>
            </div>
          </>
        )}
      </section>

      <EnvelopesPanel
        canManage={can('finance.manage')}
        customEnabled={settings.data?.custom_envelopes_enabled ?? false}
        businessTz={settings.data?.business_tz}
      />

      {editing && <AccountEditDialog account={account} onClose={() => setEditing(false)} />}
      {baseline && (
        <OpeningBalanceDialog
          account={account}
          businessTz={settings.data?.business_tz}
          onClose={() => setBaseline(false)}
        />
      )}
      {check && (
        <BankCheckDialog
          account={account}
          businessTz={settings.data?.business_tz}
          onClose={() => setCheck(false)}
        />
      )}
    </div>
  );
}
